import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { HardHat, Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await register(name, email, password);
      nav("/projects", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex blueprint-grid text-white flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-md bg-brandblue flex items-center justify-center shadow-lg">
            <HardHat className="h-6 w-6" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold text-2xl">BuildTrack</div>
            <div className="text-xs uppercase tracking-widest text-slate-300">Built for builders</div>
          </div>
        </div>
        <div className="space-y-3 max-w-md">
          <h2 className="font-display text-4xl font-bold leading-tight">Start tracking <br />in under 60 seconds.</h2>
          <p className="text-slate-300 text-base leading-relaxed">
            Free to try. Add your first project, plug in your trades, and see your budget clarity in real time.
          </p>
        </div>
        <div className="text-xs text-slate-400 uppercase tracking-widest">AUD · Mobile‑first · Site‑ready</div>
      </div>

      <div className="flex items-center justify-center px-4 py-8 sm:p-6 md:p-12 bg-background">
        <Card className="w-full max-w-md p-6 sm:p-8 border border-border shadow-sm">
          <div className="flex md:hidden items-center gap-2 mb-6">
            <div className="h-9 w-9 rounded-md bg-navy flex items-center justify-center">
              <HardHat className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display font-bold text-xl text-slate-900">BuildTrack</div>
          </div>
          <div className="mb-6 sm:mb-8">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Create account</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Set up your workspace</h1>
            <p className="text-sm text-slate-500 mt-1">Only you can see your projects.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Your name</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" className="h-12" placeholder="Alex Builder" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" className="h-12" placeholder="you@builder.com.au" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password (min 8)</Label>
              <Input id="password" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" className="h-12" placeholder="At least 8 characters" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Confirm password</Label>
              <Input id="confirm" type="password" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="register-confirm" className="h-12" placeholder="Repeat your password" />
            </div>
            {error && (
              <div className="text-sm text-status-danger-text bg-status-danger-bg border border-status-danger-border rounded-md px-3 py-2" data-testid="register-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full h-12 bg-brandblue hover:bg-brandblue-hover text-white text-base font-semibold">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Creating…" : "Create account"}
            </Button>

            <p className="text-[11px] text-slate-500 text-center leading-relaxed pt-1">
              By creating an account you agree to our{" "}
              <Link to="/terms" className="text-brandblue hover:underline font-medium" data-testid="link-terms">Terms</Link> and{" "}
              <Link to="/privacy" className="text-brandblue hover:underline font-medium" data-testid="link-privacy">Privacy Policy</Link>.
            </p>
          </form>

          <div className="text-sm text-slate-500 mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-brandblue font-semibold hover:underline" data-testid="link-login">Sign in</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
